/**
 * Google Analytics 4 Measurement Protocol sender for the admin panel.
 * Mirrors Amplitude events via direct POST to /mp/collect — no gtag.js dependency.
 * No-ops until initGA() is called with both a measurement id and api secret.
 */
import { sanitizeEventName, sanitizeParams } from './ga-mapping'
import { PropertyValue } from './types'

const MP_ENDPOINT = 'https://www.google-analytics.com/mp/collect'
const CLIENT_ID_KEY = 'ga_client_id'
const SESSION_ID_KEY = 'ga_session_id'
const LAST_ACTIVITY_KEY = 'ga_session_last_activity'
const SESSION_TIMEOUT_MS = 30 * 60 * 1000

let measurementId: string | undefined
let apiSecret: string | undefined
let currentUserId: string | undefined
let storedUserProperties: Record<string, { value: unknown }> | undefined

const generateId = (): string =>
    typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`

/** Reads a cookie value by name. Returns undefined outside the browser or when absent. */
const readCookie = (name: string): string | undefined => {
    if (typeof document === 'undefined') return undefined
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))
    return match ? decodeURIComponent(match[1]) : undefined
}

/**
 * The gtag client id, extracted from the first-party `_ga` cookie
 * (`GA1.<n>.<clientId>` where clientId = `<random>.<firstVisitTs>`). This is the SAME
 * identity gtag uses for its sessioned hits, so MP events sharing it become attributable.
 */
const getGaCookieClientId = (): string | undefined => {
    const ga = readCookie('_ga') // e.g. GA1.1.460142890.1783019225
    const match = ga?.match(/^GA\d\.\d\.(.+)$/)
    return match ? match[1] : undefined
}

/** A one-time handoff client id (e.g. from the extension via `?ga_client_id=`). */
const getHandoffClientId = (): string | undefined => {
    if (typeof window === 'undefined') return undefined
    try {
        const fromUrl = new URLSearchParams(window.location.search).get('ga_client_id')
        if (fromUrl) return fromUrl
    } catch {
        /* ignore malformed URLs */
    }
    return undefined
}

/**
 * Resolves the MP client id. Precedence (re-checked every call, never hard-cached, so a
 * later-arriving `_ga` cookie wins over an earlier fallback):
 *   1. gtag `_ga` cookie  — the canonical, sessioned identity (kills `(not set)`)
 *   2. `?ga_client_id=` handoff — extension-first path before gtag has run
 *   3. persisted UUID — truly fresh browser with no gtag/_ga yet (legacy fallback)
 */
const getClientId = (): string => {
    const gaId = getGaCookieClientId()
    if (gaId) return gaId

    const handoff = getHandoffClientId()

    try {
        const stored = localStorage.getItem(CLIENT_ID_KEY)
        if (handoff && !stored) {
            localStorage.setItem(CLIENT_ID_KEY, handoff)
            return handoff
        }
        if (stored) return stored
        const id = handoff || generateId()
        localStorage.setItem(CLIENT_ID_KEY, id)
        return id
    } catch {
        return handoff || generateId()
    }
}

/**
 * The gtag session id, auto-detected from whichever `_ga_<STREAM>` session cookie gtag
 * set on this domain (`GS<v>.<n>.s<sessionId>$...`). We don't hardcode a measurement id:
 * gtag names its per-stream session cookie `_ga_<MEASUREMENT_ID without the "G-">`, and all
 * streams in the same property share one session, so the first match is correct and self-heals
 * if the stream id ever changes. Sharing gtag's session id makes MP events land inside the same
 * GA4 session that carries the traffic source (killing `(not set)`).
 */
const getGtagSessionId = (): string | undefined => {
    if (typeof document === 'undefined') return undefined
    // Match any GA4 stream session cookie: _ga_XXXXXXXXXX=GS2.1.s<sessionId>$...
    const match = document.cookie.match(/(?:^|;\s*)_ga_[A-Z0-9]+=GS\d\.\d\.s(\d+)/)
    return match ? match[1] : undefined
}

const getSessionId = (): string => {
    const gtagSessionId = getGtagSessionId()
    if (gtagSessionId) return gtagSessionId

    // Fallback: gtag session cookie unavailable — maintain our own 30-min rolling session.
    const now = Date.now()
    try {
        const stored = sessionStorage.getItem(SESSION_ID_KEY)
        const lastActivity = sessionStorage.getItem(LAST_ACTIVITY_KEY)
        if (stored && lastActivity && now - parseInt(lastActivity, 10) < SESSION_TIMEOUT_MS) {
            sessionStorage.setItem(LAST_ACTIVITY_KEY, String(now))
            return stored
        }
        const id = String(now)
        sessionStorage.setItem(SESSION_ID_KEY, id)
        sessionStorage.setItem(LAST_ACTIVITY_KEY, String(now))
        return id
    } catch {
        return String(now)
    }
}

const send = (payload: Record<string, unknown>): void => {
    if (!measurementId || !apiSecret) return
    fetch(`${MP_ENDPOINT}?measurement_id=${encodeURIComponent(measurementId)}&api_secret=${encodeURIComponent(apiSecret)}`, {
        method: 'POST',
        body: JSON.stringify(payload),
        keepalive: true,
    }).catch(() => {
        /* GA must never break the app */
    })
}

/** Stores the measurement id and api secret. Call once at startup. */
export const initGA = (id: string, secret: string): void => {
    if (!id || !secret) return
    measurementId = id
    apiSecret = secret
}

/** Mirrors an Amplitude event to GA4 via Measurement Protocol. */
export const emitGAEvent = (eventName: string, properties?: Record<string, PropertyValue>): void => {
    if (!measurementId || !apiSecret) return
    const payload: Record<string, unknown> = {
        client_id: getClientId(),
        events: [
            {
                name: sanitizeEventName(eventName),
                params: {
                    ...sanitizeParams(properties),
                    source: 'web',
                    session_id: getSessionId(),
                    engagement_time_msec: 100,
                },
            },
        ],
    }
    if (currentUserId) payload.user_id = currentUserId
    if (storedUserProperties) payload.user_properties = storedUserProperties
    send(payload)
}

/** Mirrors Amplitude identify(): stores user_id and properties for future events. */
export const identifyGAUser = (userId: string, properties?: Record<string, PropertyValue>): void => {
    currentUserId = userId
    // Always reset first — prevents stale properties from a previous user bleeding into this session
    storedUserProperties = undefined
    if (properties && Object.keys(properties).length > 0) {
        storedUserProperties = Object.fromEntries(
            Object.entries(sanitizeParams(properties) as Record<string, unknown>).map(([k, v]) => [
                k,
                // GA4 MP user_properties values must be string or number — coerce booleans
                { value: typeof v === 'boolean' ? String(v) : v },
            ])
        )
    }
}

/** Mirrors Amplitude reset(): clears stored user_id and properties. */
export const resetGAUser = (): void => {
    currentUserId = undefined
    storedUserProperties = undefined
}

declare global {
    interface Window {
        dataLayer: unknown[]
        gtag: (...args: unknown[]) => void
    }
}
